import { useEffect, useRef } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn, tables as minetwinTables } from '@sdk/minetwin';
import { useAuthKit } from '@/contexts/AuthProvider';

/**
 * One-shot reconciler: if the caller's `user.display_name` was filled in by
 * `client_connected`'s email fallback (because the JWT access token carries
 * no `name` claim), backfill it with the proper "First Last" string from
 * AuthKit's user object via the `set_user_display_name` reducer.
 *
 * Runs whenever `my_user` updates. Idempotent across reconnects:
 *   - If display_name is already the proper name (not equal to email),
 *     the condition fails and nothing fires.
 *   - If AuthKit hasn't surfaced firstName/lastName yet, we wait.
 *
 * Per-session de-dup via a ref so the reducer isn't called twice during a
 * single subscription burst (the table updates again after the write).
 */
export function useDisplayNameSync(): void {
  const { user } = useAuthKit();
  const minetwin = useSpacetimeDB('minetwin');
  const [myUserRows] = useTable('minetwin', minetwinTables.my_user);
  const me = myUserRows[0] ?? null;
  const calledForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!me || !user) return;

    const fullName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (!fullName) return; // AuthKit hasn't loaded the profile yet
    if (me.displayName === fullName) return; // Already correct
    if (me.displayName !== me.email) return; // User set a custom name; don't clobber

    // Only call once per user_id per session.
    if (calledForRef.current === me.userId) return;

    const conn = minetwin.getConnection() as MinetwinConn | null;
    if (!conn) return;

    try {
      conn.reducers.setUserDisplayName({
        userId: me.userId,
        displayName: fullName,
      });
      calledForRef.current = me.userId;
      console.info(
        '[display-name-sync] set display_name → %s for %s',
        fullName,
        me.email,
      );
    } catch (err) {
      console.error('[display-name-sync] reducer call failed', err);
    }
  }, [me, user, minetwin]);
}
